import { Component, OnInit } from '@angular/core';
import { CommonStateService } from 'src/state/common/service';
import { CalsiumListConfig, CalsiumListSelectItem, ColumnDataType } from 'src/app/calsiumlist/calsiumlist.component.model';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subscription, combineLatest, firstValueFrom, from } from 'rxjs';
import { Market, Paging } from 'src/state';
import { debounceTime, distinctUntilChanged, map, skip, switchMap } from 'rxjs/operators';
import { MissingSIMMaterial } from 'src/state/missing-sim-material/state-models/missing-sim-material';
import { MissingSIMMaterialListStateService } from 'src/state/missing-sim-material/service';
import { MaterialSearchForm } from 'src/state/missing-sim-material/state-models/missing-material-search-form';
import { ErrorHandlerService } from 'src/app/errorhandler/error-handler.service';
import { MarketApiService } from 'src/api/services/market-api.service';
import { ErrorTypeEnum } from 'src/app/shared/enums.model';
import { ActivatedRoute, ParamMap } from '@angular/router';

@Component({
    selector: 'cal-missing-sim-materials',
    templateUrl: './missing-sim-materials.component.html',
    styleUrls: ['./missing-sim-materials.component.css'],
    standalone: false
})
export class MissingSIMMaterialsComponent implements OnInit {

  resources: any = this.commonService.getResources();
  searchComponentsForm!: UntypedFormGroup;
  materialSearchForm!: Observable<MaterialSearchForm>
  listConfig!: CalsiumListConfig;
  showHint = true;
  dataList!: Observable<MissingSIMMaterial[]>;
  paging!: Observable<Paging>;
  totalCount!: Observable<number>;
  subscriptions = new Subscription();
  isLoading!: Observable<boolean>;
  sortingExpression!: Observable<string>;
  filterExpression!: Observable<string>;
  marketList!: Observable<Market[]>;
  selectedMarketId: number = 0;

  constructor(
    private commonService: CommonStateService,
    private missingSIMMaterialService: MissingSIMMaterialListStateService,
    private errHandler: ErrorHandlerService,
    private formBuilder: UntypedFormBuilder,
    private marketApi: MarketApiService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.isLoading = this.commonService.getLoading();
    this.sortingExpression = this.missingSIMMaterialService.getSortingExpression();
    this.filterExpression = this.missingSIMMaterialService.getFilterExpression();
    this.totalCount = this.missingSIMMaterialService.getTotalCount();
    this.paging = this.missingSIMMaterialService.getPaging();
    this.dataList = this.missingSIMMaterialService.getList();
    this.materialSearchForm = this.missingSIMMaterialService.getMaterialSearchForm();

    const selectedMarketId = this.getMarketId(this.route.snapshot.queryParamMap);
    this.fillSearchForm(selectedMarketId);

    if (selectedMarketId !== null) {
      this.missingSIMMaterialService.changeMaterialSearchForm({ marketId: selectedMarketId });
    }

    let pageInitialLoad = selectedMarketId === null;
    this.subscriptions.add(
      combineLatest(this.materialSearchForm, this.paging, this.sortingExpression, this.filterExpression).pipe(
        debounceTime(0),
        switchMap(([searchForm, paging, sortingExpression, filterExpression]) => {
          if (!pageInitialLoad) {
            return this.missingSIMMaterialService.loadMissingSIMMaterialList(searchForm,
              paging,
              sortingExpression,
              filterExpression,
              true
            );
          }
          pageInitialLoad = false;
          return Promise.resolve();
        })
      ).subscribe()
    );

    this.subscriptions.add(
      this.route.queryParamMap.pipe(
        map(params => this.getMarketId(params)),
        distinctUntilChanged(),
        skip(1)
      ).subscribe(marketId => {
        if (marketId !== null) {
          this.searchComponentsForm.patchValue({ selectedMarket: marketId });
          this.missingSIMMaterialService.changeMaterialSearchForm({ marketId });
        }
      })
    );

    this.marketList = from(this.marketApi.getMarket());
    this.buildList();

  }

  ngOnDestroy() { this.subscriptions.unsubscribe(); }

  fillSearchForm(market: number | null) {
    this.searchComponentsForm = this.formBuilder.group({
      selectedMarket: [market, <any>Validators.required]
    });
  }

  private getMarketId(params: ParamMap): number | null {
    const marketId = Number(params.get('marketId'));
    return params.has('marketId') && Number.isInteger(marketId) ? marketId : null;
  }

  buildList() {
    this.listConfig = new CalsiumListConfig();
    this.listConfig.ListCaption = this.resources.List;
    this.listConfig.ShowFilterRow = true;
    this.listConfig.ParentComponent = this;
    this.listConfig.ShowExportToExcel = true;
    let column;
    column = this.listConfig.AddListColumn(ColumnDataType.number, 'materialNumber', this.resources.MaterialNumber);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'materialName', this.resources.MaterialName);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'componentType', this.resources.ComponentType);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'purchaserCode', this.resources.PurchaserCode);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'slaTypeId', this.resources.SlaType);
    column.AllowFilter = false;
    column.Sortable = false;

    column = this.listConfig.AddListColumn(ColumnDataType.list, 'marketId', this.resources.Market);
    column.DisplayColumnName = 'market.name';
    column.AllowFilter = false;
    column.Sortable = false;
    column.FilterOptions.FilterListItems = this.marketList.pipe(
     map(markets =>
       markets.map(market =>
         CalsiumListSelectItem.getInstance(
           market.id.toString(),
           market.name
         )
       )
     )
    );
column.FilterOptions.AddEmptyDropDownListItem = false;

}

  onPaging(page: number) {
    this.missingSIMMaterialService.goToPage(page);
  }

  onSort(sortExpression: string) {
    this.missingSIMMaterialService.sort(sortExpression);
  }

  onFilter(filterExpression: string) {
    this.missingSIMMaterialService.filter(filterExpression);
  }

  onClearFilterAndSorting() {
    this.missingSIMMaterialService.clearFilterAndSorting();
  }

  onSearch() {
    if (this.isValidForm()) {
      if (this.searchComponentsForm.valid) {
        const marketId = Number(this.searchComponentsForm.value.selectedMarket);
        const param: MaterialSearchForm = {
          marketId
        };
        this.missingSIMMaterialService.changeMaterialSearchForm(param);
      }
    }
  }


  async onExportToExcel() {
    if (!this.isValidForm()) return;

    const marketId = Number(this.searchComponentsForm.value.selectedMarket);

    const markets = await firstValueFrom(this.marketList);
    const marketName = marketId === 0 ? 'All' : (markets.find(x => x.id === marketId)?.name);

    const fileName = `MissingSimMaterials_${marketName}`;
    const payload = {
      marketId
    };

    return this.missingSIMMaterialService.exportMissingMaterialExcel(fileName, payload);
  }
  
  onHintDismiss(): void {
    this.showHint = false;
}

  isValidForm(): boolean {
    let valid = true;
    const selectedMarket = this.searchComponentsForm.get('selectedMarket')?.value;
    if (selectedMarket === null || selectedMarket === undefined || selectedMarket === '') {
      this.errHandler.emitError(
        this.resources.MarketRequired,
        this.resources.Error,
        ErrorTypeEnum.Error);
      valid = false;
    }

    return valid;
  }

}
